import { parseExcelDate } from "../../../utils/data/excelData";

export const PRIOR_YA_SECTIONS = ["CA", "Losses", "Donations"];

export const getPriorYAHeaders = (isCrossYA) => [
  "Section",
  "Year of Assessment / Financial year ended",
  ...(isCrossYA ? ["Preceding Amount", "Current Amount"] : ["Amount"]),
  "Reference",
  "Exchange Rate",
  "Forfeited",
  "Declare Agreement Status",
  "Agreed",
  "Date of Notice of Assessment"
];

export const parsePriorYARows = (data, { isCrossYA, yearOfAssessment, currency = "SGD", currencyRate }) => {
  const rows = [];
  const errors = [];
  const headers = getPriorYAHeaders(isCrossYA);
  const uploadedHeaders = Array.from(data[0] || [], (value) => String(value ?? "").trim());
  while (uploadedHeaders.length && uploadedHeaders[uploadedHeaders.length - 1] === "") uploadedHeaders.pop();
  if (uploadedHeaders.length !== headers.length || !headers.every((header, i) => uploadedHeaders[i] === header)) {
    return { rows, errors: [`Row 1: Invalid template for ${isCrossYA ? "cross YA (Preceding Amount and Current Amount required)" : "single YA (Amount required)"}. Download the template for this Main Tax Computation. Expected columns in order: ${headers.join(", ")}.`] };
  }
  data.slice(1).forEach((values, index) => {
    if (values.every((value) => value == null || String(value).trim() === "")) return;
    const issues = [];
    const section = String(values[0] ?? "").trim();
    if (!PRIOR_YA_SECTIONS.includes(section)) {
      errors.push(`Row ${index + 2}: Column 1 must be one of CA, Losses, Donations. Found: '${section}'.`);
      return;
    }
    const isCA = section === "CA";
    const year = Number(values[1]);
    const date = isCA ? null : parseExcelDate(values[1]);
    if (isCA ? !/^\d{4}$/.test(String(values[1]).trim()) || year >= Number(yearOfAssessment)
      : !date || date.getFullYear() > Number(yearOfAssessment) - 2) {
      issues.push(isCA ? "Column 2 must be a four-digit prior YA." : "Column 2 must be a valid prior financial year end (DD/MM/YYYY), before the current YA's financial year.");
    }
    const amounts = (isCrossYA ? [2, 3] : [2]).map((column) => {
      const value = values[column];
      const text = String(value ?? "").trim();
      const amount = Number(text.replace(/,/g, ""));
      if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(text) || !Number.isFinite(amount)) {
        issues.push(`Column ${column + 1} must be a non-negative amount.`);
      }
      return amount;
    });
    const exchangeRateColumn = isCrossYA ? 5 : 4;
    let exchangeRate = 1;
    if (currency !== "SGD") {
      const text = String(values[exchangeRateColumn] ?? "").trim();
      exchangeRate = text === "" ? Number(currencyRate) : Number(text);
      if ((text !== "" && !/^\d+(?:\.\d+)?$/.test(text)) || !Number.isFinite(exchangeRate) || exchangeRate <= 0) {
        issues.push(`Column ${exchangeRateColumn + 1} must be a positive exchange rate. Leave blank to use the Main Tax Computation exchange rate.`);
      }
    }
    const optionalStart = exchangeRateColumn + 1;
    const parseOptionalBoolean = (column) => {
      const text = String(values[column] ?? "").trim().toLowerCase();
      if (text === "" || text === "false") return false;
      if (text === "true") return true;
      issues.push(`Column ${column + 1} must be TRUE, FALSE or blank.`);
      return false;
    };
    const isForfeited = parseOptionalBoolean(optionalStart);
    const toDeclareAgreement = parseOptionalBoolean(optionalStart + 1);
    // Agreement details are neither validated nor imported unless declared.
    const isAgreed = toDeclareAgreement
      ? parseOptionalBoolean(optionalStart + 2)
      : null;
    let dateOfNOA = null;
    if (toDeclareAgreement) {
      const value = values[optionalStart + 3];
      if (value != null && String(value).trim() !== "") {
        dateOfNOA = parseExcelDate(value);
        if (!dateOfNOA) {
          issues.push(`Column ${optionalStart + 4} must be a valid date (DD/MM/YYYY) or blank.`);
        }
      }
    }
    if (issues.length) {
      errors.push(`Row ${index + 2}: ${issues.join(" | ")}`);
      return;
    }
    const amount = amounts.reduce((sum, value) => sum + value, 0);
    rows.push({
      section,
      ...(isCA ? { yearOfAssessment: year } : { date }),
      amount,
      historyAmount: [amount],
      reference: String(values[isCrossYA ? 4 : 3] ?? "").trim(),
      schedule: "",
      exchangeRate,
      isForfeited,
      toDeclareAgreement,
      agreementStatus: { dateOfNOA, isAgreed },
      ...(isCrossYA ? {
        preceding_amount: amounts[0], current_amount: amounts[1],
        precedingHistoryAmount: [amounts[0]], currentHistoryAmount: [amounts[1]]
      } : {})
    });
  });
  return { rows, errors };
};
